// هزینه بلیط ماموریت
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, qs, num, curYear, splitLines, visibleCols, runReport, MsgBox, AskBox, Field, DateField, SelectField, Box,
  Lookup, Grid, Modal, tbtn,
} from './RequestsShared';

const FORM = 'FRMFLIGHTTICKET';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNNEWREQUEST',
  'BTNFLIGHTTICKETCONFIRM', 'BTNFLIGHTTICKETREQUEST'];
const TITLE = 'هزینه بلیط ماموریت';
const VEHICLES = ['هواپیما', 'قطار', 'خودرو'];

export const COLS = [
  { key: 'vehicle', label: 'وسیله نقلیه' },
  { key: 'asker', label: 'درخواست کننده', wrap: true },
  { key: 'department', label: 'واحد اجرایی', wrap: true },
  { key: 'amount_receiver', label: 'فروشنده', wrap: true },
  { key: 'passenger', label: 'مسافر', wrap: true },
  { key: 'ticket_no', label: 'شماره بلیط' },
  { key: 'flight_date', label: 'تاریخ' },
  { key: 'source', label: 'مبدا', wrap: true },
  { key: 'target', label: 'مقصد', wrap: true },
  { key: 'flight_cost', label: 'مبلغ', money: true },
  { key: 'added_tax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'remark', label: 'شرح', wrap: true },
  { key: 'request_no', label: 'شماره درخواست', num: true },
  { key: 'request_date', label: 'تاریخ درخواست' },
  { key: 'confirm_no', label: 'شماره تایید', num: true },
  { key: 'confirm_date', label: 'تاریخ تایید' },
  { key: 'hazineh_document_no', label: 'شماره سندهزینه', num: true },
  { key: 'hazineh_document_date', label: 'تاریخ سندهزینه' },
  { key: 'user_group', label: 'گروه کاربری' },
];

const KEYS = ['vehicle_id', 'asker_id', 'asker', 'department_id', 'department', 'amount_receiver_id', 'amount_receiver',
  'people_id', 'passenger', 'ticket_no', 'flight_date', 'source_city_id', 'source', 'target_city_id', 'target',
  'flight_cost', 'added_tax', 'remark', 'request_no', 'request_date', 'confirm_no', 'confirm_date',
  'hazineh_document_no', 'hazineh_document_date'];
const pick = r => KEYS.reduce((o, k) => ({ ...o, [k]: r ? (r[k] ?? '') : '' }), {});

export default function FlightTicket({ user, onBack }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [year] = useState(curYear());
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(pick(null));
  const [formErr, setFormErr] = useState('');
  const [newReq, setNewReq] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(false);
  const [saving, setSaving] = useState(false);

  const L = usePagedList((page) => API(`/flight/?page=${page}`));
  const reload = useCallback((keepId) => {
    L.reload({}).then(j => {
      if (!j || !j.ok) return;
      const rows = j.data || [];
      const hit = keepId ? rows.find(r => r.id === keepId) : null;
      setSelected(hit || rows[0] || null);
    });
  }, [L]);

  useEffect(() => { if (fb.loaded && fb.open) reload(); }, [fb.loaded, fb.open]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const has = !!selected;

  const openNew = async () => {
    const j = await API(`/flight/defaults/?year=${year}`);
    if (!j.ok) { showMsg(j.msg); return; }
    const s = selected || {};
    // شرح، مسافر، درخواست‌کننده، واحد و فروشنده از رکورد جاری می‌ماند
    setForm({
      ...pick(null), vehicle_id: s.vehicle_id || 0,
      asker_id: s.asker_id || 0, asker: s.asker || '', department_id: s.department_id || 0, department: s.department || '',
      amount_receiver_id: s.amount_receiver_id || 0, amount_receiver: s.amount_receiver || '',
      people_id: s.people_id || 0, passenger: s.passenger || '', remark: s.remark || '',
      ...j.data,
    });
    setFormErr(''); setNewReq(true); setModal('new');
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const openEdit = async () => {
    if (!selected) return;
    const j = await API(`/flight/${selected.id}/check-edit/`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(pick(selected)); setFormErr(''); setNewReq(false); setModal('edit');
  };
  const save = async () => {
    setFormErr(''); setSaving(true);
    const body = JSON.stringify({ ...form, year });
    const j = modal === 'new'
      ? await API('/flight/create/', { method: 'POST', body })
      : await API(`/flight/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) {
      setModal(null); reload(j.id);
      if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
    } else {
      setFormErr(j.msg);
      if (j.need_new_request) setNewReq(true);
    }
  };
  const newRequestNo = async () => {
    const j = await API(`/flight/request-no/?${qs({ year, request_date: form.request_date, confirm_date: form.confirm_date })}`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(f => ({ ...f, request_no: j.request_no, confirm_no: j.confirm_no }));
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const taxKey = e => {
    if (e.key === 'F11') {
      e.preventDefault();
      const p = num(form.added_tax);
      if (p <= 100) F('added_tax', String(Math.round(num(form.flight_cost) * p / 100)));
    }
  };
  const doDelete = async () => {
    setAsk(false);
    const j = await API(`/flight/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) reload(); else showMsg(j.msg);
  };

  const shown = visibleCols(COLS, L.data);
  const toolbar = (
    <>
      {fb.open && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={tbtn(ui.btnNew)}>＋ جدید</button>}
          {fb.can('BTNEDIT') && L.data.length > 0 && <button onClick={openEdit} disabled={!has} style={tbtn(ui.btnEdit, has)}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && L.data.length > 0 && <button onClick={() => has && setAsk(true)} disabled={!has} style={tbtn(ui.btnDel, has)}>✕ حذف</button>}
          {fb.can('BTNFLIGHTTICKETCONFIRM') && has && (
            <button onClick={() => runReport('tadarokat.flight_ticket_confirm', { confirm_no: selected.confirm_no,
              confirm_date: selected.confirm_date, request_date: selected.request_date }, showMsg)}
            style={tbtn(ui.btnItems)}>تاییدیه</button>)}
          {fb.can('BTNFLIGHTTICKETREQUEST') && has && (
            <button onClick={() => runReport('tadarokat.flight_ticket_request',
              { request_no: selected.request_no, request_date: selected.request_date }, showMsg)}
            style={tbtn(ui.btnItems)}>برگه درخواست</button>)}
        </div>
      )}
      <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="بلیط" />
      <ExcelButton title={TITLE} columns={shown} rows={L.data} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={TITLE} subtitle="تدارکات — سامانه ارجان" toolbar={toolbar}
        error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit="بلیط">
        {fb.open && (
          <Grid columns={shown} rows={L.data} selectedId={selected && selected.id} onSelect={setSelected}
            loading={L.loading} empty="بلیطی ثبت نشده است" />
        )}
      </ListPage>

      {modal && (
        <Modal title={modal === 'new' ? 'بلیط جدید' : 'ویرایش بلیط'} onClose={() => setModal(null)}
          footer={<>
            {newReq && fb.can('BTNOK') && <button onClick={newRequestNo} style={tbtn(ui.btnItems)}>شماره درخواست جدید</button>}
            {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
            {fb.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳' : 'ثبت'}</button>}
          </>}>
          {formErr && <div style={{ ...ui.errBox, whiteSpace: 'pre-line' }}>⚠️ {formErr}</div>}
          <div style={ui.row}>
            <Field label="شماره بلیط" value={form.ticket_no} onChange={v => F('ticket_no', v)} ltr />
            <DateField label="تاریخ" value={form.flight_date} onChange={v => F('flight_date', v)} />
            <SelectField label="وسیله" value={Number(form.vehicle_id) || 0} options={VEHICLES} onChange={v => F('vehicle_id', v)} />
            <Lookup label="مسافر" value={form.passenger} endpoint="/flight/lookup/people/"
              onPick={r => setForm(f => ({ ...f, people_id: r.id, passenger: r.title }))} />
          </div>
          <div style={ui.row}>
            <Lookup label="مبدا" value={form.source} endpoint="/flight/lookup/cities/"
              onPick={r => setForm(f => ({ ...f, source_city_id: r.id, source: r.title }))} />
            <Lookup label="مقصد" value={form.target} endpoint="/flight/lookup/cities/"
              onPick={r => setForm(f => ({ ...f, target_city_id: r.id, target: r.title }))} />
            <Field label=" مبلغ" value={form.flight_cost} onChange={v => F('flight_cost', v)} ltr />
            <Field label="مالیات بر ارزش افزوده" value={form.added_tax} onChange={v => F('added_tax', v)} onKeyDown={taxKey} ltr
              hint="درصد را وارد و F11 را بزنید" />
          </div>
          <div style={ui.row}>
            <Lookup label="فروشنده/گیرنده وجه" value={form.amount_receiver} endpoint="/flight/lookup/amount-receivers/"
              onPick={r => setForm(f => ({ ...f, amount_receiver_id: r.id, amount_receiver: r.title }))} />
            <Field label="توضیح" value={form.remark} onChange={v => F('remark', v)} flex={2} />
          </div>
          <div style={ui.row}>
            <Lookup label="درخواست کننده" value={form.asker} endpoint="/flight/lookup/people/"
              onPick={r => setForm(f => ({ ...f, asker_id: r.id, asker: r.title }))} />
            <Lookup label="واحد اجرایی" value={form.department} endpoint="/flight/lookup/departments/"
              onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.title }))} />
          </div>
          <div style={ui.row}>
            <Box title="درخواست">
              <Field label="شماره" value={form.request_no} onChange={v => F('request_no', v)} ltr />
              <DateField label="تاریخ" value={form.request_date} onChange={v => F('request_date', v)} />
            </Box>
            <Box title="تایید">
              <Field label="شماره" value={form.confirm_no} onChange={v => F('confirm_no', v)} ltr />
              <DateField label="تاریخ" value={form.confirm_date} onChange={v => F('confirm_date', v)} />
            </Box>
            <Box title="سند هزینه">
              <Field label="شماره" value={form.hazineh_document_no || ''} disabled />
              <Field label="تاریخ" value={form.hazineh_document_date || ''} disabled />
            </Box>
          </div>
        </Modal>
      )}

      {ask && <AskBox lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} danger onYes={doDelete} onNo={() => setAsk(false)} />}
      {msg && <MsgBox lines={msg} onClose={() => setMsg(null)} />}
    </>
  );
}
